import React, { useState, useEffect } from 'react';

import { Skeleton } from '@mui/material';
import { t } from 'i18next';

import CoverageGraphSection from './sections/CoverageGraphSection';
import BdsCircleIndicatorPictogram from '../../../../../../shared/components/v3/BdsCircleIndicator/BdsCircleIndicatorPictogram/BdsCircleIndicatorPictogram';
import BdsCardContainer from '../../../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import ErrorCard from '../../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import { INSURANCE_COVERAGE } from '../../../../../../shared/constants/images';
import { IMetricsProtectionCoverage } from '../../../../../../shared/interfaces/metrics';
import { MOCK_METRICS_PROTECTION_COVERAGE } from '../../../../../../shared/mocks/metrics';
import { TRANSLATIONS_GLOBAL } from '../../../../../../shared/translations/commons';
import { TRANSLATIONS_METRICS_NE } from '../../../../../../shared/translations/metrics';
import { GenericFunction } from '../../../../../../shared/types/genericTypes';
import { toLocaleNumberFormat } from '../../../../../../shared/utils/utils';
import './CoverageGraph.scss';

interface ICoverageGraph {
  coverageData?: IMetricsProtectionCoverage;
  isLoading?: boolean;
  isError?: boolean;
  fetch: GenericFunction;
}
const CoverageGraph = ({ coverageData, isError, isLoading, fetch }: ICoverageGraph) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [emptyData, setEmptyData] = useState<boolean>(false);

  useEffect(() => {
    if (!coverageData || coverageData?.periods?.length === 0) {
      setEmptyData(true);
    } else {
      setEmptyData(false);
    }
  }, [coverageData]);

  const handleAction = () => {
    fetch();
  };

  const coveragePointsDataRender = (pointData: { title: string; value: number | null }, isTotal: boolean) => {
    const className = ['item', isTotal ? 'total' : 'base', emptyData ? 'no-data' : ''].join(' ');
    const currency = emptyData ? MOCK_METRICS_PROTECTION_COVERAGE.currency : coverageData?.currency;
    const baseIndex = 99;

    return (
      <div className="data">
        <button
          onMouseEnter={() => setHoveredIndex(isTotal ? null : baseIndex)}
          onMouseLeave={() => setHoveredIndex(null)}
          className={`value ${className}`}
        >{`${toLocaleNumberFormat(pointData.value ?? 0)} ${currency}`}</button>
        <span className={`title ${className}`}>{pointData.title}</span>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="coverage-graph-skeleton-container">
        <Skeleton className="coverage-graph-card-skeleton" variant="rectangular" />
      </div>
    );
  }
  if (isError) {
    return (
      <ErrorCard
        primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
        secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
        action={handleAction}
        buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
      />
    );
  }

  return (
    <BdsCardContainer
      borderRadiusSize="radius-l"
      paddingSize="padding-l"
      className="coverage-graph-container flex flex-col justify-between"
    >
      <div className="coverage-top">
        <div className="ct-title">
          <BdsCircleIndicatorPictogram
            circleIndicatorSize="l"
            pictogram={{ component: INSURANCE_COVERAGE }}
            className="ctt-pictogram"
          />
          <div className="ctt-text">{t(TRANSLATIONS_METRICS_NE.protections.coverageGraph.title)}</div>
        </div>
        <div className="ct-data">
          {coveragePointsDataRender(
            {
              title: t(TRANSLATIONS_METRICS_NE.protections.coverageGraph.textBase),
              value: coverageData?.baseCoverage ?? 0
            },
            false
          )}
          {coveragePointsDataRender(
            {
              title: t(TRANSLATIONS_METRICS_NE.protections.coverageGraph.textTotal),
              value: coverageData?.totalCoverage ?? 0
            },
            true
          )}
        </div>
      </div>
      <CoverageGraphSection
        tooltipMessage={
          emptyData
            ? TRANSLATIONS_METRICS_NE.protections.coverageGraph.toolTipEmptyHover
            : TRANSLATIONS_METRICS_NE.protections.coverageGraph.toolTipHover
        }
        coverageData={emptyData ? MOCK_METRICS_PROTECTION_COVERAGE : coverageData}
        isEmpty={emptyData}
        hoveredIndex={hoveredIndex}
        setHoveredIndex={setHoveredIndex}
      />
    </BdsCardContainer>
  );
};

export default CoverageGraph;
